<template>
  <v-card class="project-files-card">
    <v-card-title>
      {{ title }}
      <div class="project-files-card__details">
        {{ currentTabFiles.length }} z {{ projectFilesCountSuffix }}
      </div>
    </v-card-title>

    <v-card-text class="project-files-card__content">
      <template v-if="!hideTabs">
        <v-tabs v-model="activeTabIndex">
          <v-tab
            v-for="tab in staticData.tabs"
            :key="tab.label"
          >
            {{ tab.label }}
          </v-tab>
        </v-tabs>
      </template>

      <v-sheet
        color="accent-grey"
        class="pa-4"
      >
        <div class="d-flex align-center justify-space-between mb-4">
          <v-switch
            :value="areAllFilesSelected"
            color="primary"
            class="mt-0"
            dense
            hide-details
            @change="toggleSelection"
          >
            <template #label>
              <span class="primary--text text-body-2 font-weight-medium"> Zaznacz wszystkie </span>
            </template>
          </v-switch>

          <v-spacer />

          <AppViewToggle v-model="viewMode" />
        </div>
        <div class="py-4">
          <AppFilesList
            v-if="viewMode === 'list'"
            v-model="selectedFiles"
            :files="currentTabFiles"
          />

          <AppFilesTable
            v-else
            v-model="selectedFiles"
            :files="currentTabFiles"
          />
        </div>

        <div
          v-if="editable || !disableAddition || !disableDownload || !disableDelete"
          class="actions-wrapper"
        >
          <v-btn
            v-if="!disableAddition"
            depressed
            color="primary"
            @click="addFiles"
          >
            Dodaj pliki
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="!disableDownload"
            :disabled="!selectedFiles.length"
            color="primary"
            outlined
            class="mr-4"
            :loading="processing"
            @click="downloadFiles(selectedFiles)"
          >
            Pobierz
          </v-btn>

          <v-btn
            v-if="editable || !disableDelete"
            :disabled="!selectedFiles.length"
            depressed
            color="primary"
            outlined
            class="mr-4"
            :loading="processing"
            @click="confirmDeleteFiles"
          >
            Usuń
          </v-btn>

          <template v-if="editable">
            <v-btn
              v-if="!commercialPrintFiles"
              color="primary"
              outlined
              :disabled="isThumbnailDisabled"
              class="mr-4"
              @click="setThumbnal"
            >
              Ustaw miniaturkę
            </v-btn>

            <v-menu
              v-if="!commercialPrintFiles"
              offset-y
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="!selectedFiles.length"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  Przenieś
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>

              <v-list min-width="160px">
                <v-list-item
                  v-for="movement in possibleMovements"
                  :key="movement.value"
                  dense
                  exact
                  @click="updateFiles({ status: movement.value, fileIds: selectedFiles })"
                >
                  {{ movement.label }}
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </div>
      </v-sheet>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import utils from '@/helpers/utils';
import AppFilesList from '@/components/UI/AppFilesList.vue';
import AppFilesTable from '@/components/UI/AppFilesTable.vue';
import AppEmptyState from '@/components/UI/AppEmptyState.vue';
import AppViewToggle from '@/components/UI/AppViewToggle.vue';

const tabs = [
  { label: 'Robocze', value: ['brief', 'draft'] },
  { label: 'Do akceptacji', value: ['accepted'] },
  { label: 'Archiwum', value: ['archived'] },
];

const movementsList = [
  { value: 'archive', label: 'Do archiwum', statuses: ['brief', 'draft', 'accepted'] },
  { value: 'accept', label: 'Do akceptacji', statuses: ['brief', 'draft', 'archived'] },
  { value: 'restore_status', label: 'Przywróć pierwotny status', statuses: ['archived', 'accepted'] },
];

export default {
  components: {
    AppFilesList,
    AppFilesTable,
    AppEmptyState,
    AppViewToggle,
  },

  props: {
    files: {
      type: Array,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    disableAddition: {
      type: Boolean,
      default: false,
    },
    disableDownload: {
      type: Boolean,
      default: false,
    },
    disableDelete: {
      type: Boolean,
      default: false,
    },
    hideTabs: {
      type: Boolean,
      default: false,
    },
    processing: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Baza plików',
    },
    kiwFiles: {
      type: Boolean,
      default: false,
    },
    commercialPrintFiles: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedFiles: [],
      viewMode: 'list',
      activeTabIndex: 0,
      staticData: {
        tabs,
      },
    };
  },

  computed: {
    ...mapState('project', ['isProcessing']),
    projectFilesCountSuffix() {
      return utils.getCounterWithSuffix(this.files.length, 'plik', 'pliki', 'plików');
    },
    currentTabStatus() {
      return this.staticData.tabs[this.activeTabIndex].value;
    },
    possibleMovements() {
      return movementsList.filter((movement) =>
        movement.statuses.some((status) => this.currentTabStatus.includes(status))
      );
    },
    currentTabFiles() {
      return this.hideTabs
        ? this.files
        : this.files.filter((file) => this.currentTabStatus.includes(file.currentStatus));
    },
    areAllFilesSelected() {
      return this.currentTabFiles.length && this.currentTabFiles.length === this.selectedFiles.length;
    },
    firstFileObject() {
      return this.files.find((file) => file.id === this.selectedFiles[0]);
    },
    isThumbnailDisabled() {
      const isThumbnailAllowed = () => {
        return ['image', 'video', 'pdf'].some((type) => this.firstFileObject?.mimeType?.includes(type));
      };
      return this.selectedFiles.length !== 1 || !isThumbnailAllowed();
    },
  },

  watch: {
    activeTabIndex() {
      this.selectedFiles = [];
    },
  },

  methods: {
    ...mapActions('dialog', ['showDialog', 'clearDialog']),
    ...mapActions('project', ['changeFilesStatus', 'downloadProjectFiles', 'selectThumbnail']),
    ...mapActions('kiwSingle', ['downloadKIWFiles']),
    ...mapActions('commercialPrintSingle', ['downloadCommercialPrintFiles']),

    async downloadFiles(fileIds) {
      try {
        if (this.commercialPrintFiles) {
          await this.downloadCommercialPrintFiles(fileIds);
        } else if (this.kiwFiles) {
          await this.downloadKIWFiles(fileIds);
        } else {
          await this.downloadProjectFiles(fileIds);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.clearSelection();
      }
    },
    updateFiles({ status, fileIds }) {
      this.changeFilesStatus({ status, fileIds }).then(() => {
        this.selectedFiles = this.selectedFiles.filter((fileId) => !fileIds.includes(fileId));
      });
    },
    addFiles() {
      this.$emit('add');
    },
    setThumbnal() {
      const mimeType = this.firstFileObject?.mimeType;

      if (mimeType.includes('pdf')) {
        this.showDialog({
          header: 'Ustaw miniaturkę',
          component: 'SetPdfThumbnail',
          props: {
            file: this.firstFileObject,
          },
        });
      } else if (mimeType.includes('video')) {
        this.showDialog({
          header: 'Ustaw miniaturkę',
          component: 'SetVideoThumbnail',
          props: {
            file: this.firstFileObject,
          },
        });
      } else {
        this.selectThumbnail({ id: this.selectedFiles[0], type: 'image' });
      }
      this.clearSelection();
    },
    clearSelection() {
      this.selectedFiles.splice(0, this.selectedFiles.length);
    },
    toggleSelection() {
      if (this.areAllFilesSelected) {
        this.clearSelection();
      } else {
        this.selectedFiles = this.currentTabFiles.map((file) => file.id);
      }
    },
    confirmDeleteFiles() {
      this.showDialog({
        header: 'Usuwanie plików',
        text: 'Czy na pewno chcesz usunąć pliki?',
        component: 'Confirm',
        props: {
          confirmText: 'Usuń',
          rejectText: 'Nie usuwaj',
        },
      })
        .then(() => {
          this.$emit('delete', this.selectedFiles);
        })
        .catch(() => {
          this.clearDialog();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.project-files-card {
  &__details {
    font-size: 10px;
    line-height: 15px;
    letter-spacing: 0.5px;
    font-weight: 400;
  }
  &__content {
    min-height: calc(176px - 102px);
  }
  &::v-deep .v-tab {
    border-radius: 8px 8px 0 0;
    color: $primary !important;
  }
  &::v-deep .v-tab.v-tab--active {
    background-color: $accent-grey;
  }
}

.actions-wrapper {
  padding-top: 16px;
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid #e4e4e4;
}
</style>
